<script setup>
import {useUserGroupsStore, useUsersStore, useTagsStore} from '@/stores'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useDesignSystem } from '@/composables/useDesignSystem'
import AppDateTimePicker from '@core/components/AppDateTimePicker.vue'
import UserDeliveryHealthBadge from '@core/components/UserDeliveryHealthBadge.vue'
import CommsHistoryPanel from '@core/components/CommsHistoryPanel.vue'
import AdminNotificationPreferences from '@core/components/AdminNotificationPreferences.vue'
import MemberCommitmentAdminPanel from '@core/components/MemberCommitmentAdminPanel.vue'
import {
  emailValidator, nameValidator,
  requiredValidator,
  profileIdValidator,
} from '@validators'
import { onMounted, watch } from 'vue'
import axios from '@axios'
const emit = defineEmits([
  'update:modelValue',
  'submit',
  'update:isDialogVisible'
])

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('md')

const tagsStore = useTagsStore()
const usersStore = useUsersStore()
let adminEditingUser = null
let myUser = null
let fetching = null;
({ myUser, adminEditingUser, fetching } = storeToRefs(usersStore))

const userGroupsStore = useUserGroupsStore()
let userGroups = null;
({ userGroups } = storeToRefs(userGroupsStore))

const isPlatformAdmin = computed(() => myUser.value?.userLevel === 'admin')

// RM-359: Star is internal and never in a user payload, so the dialog reads it
// from its admin-only endpoint each time a member is opened. Until it loads,
// rmStar stays undefined, which the save leaves untouched.
watch(() => adminEditingUser.value?._id, async id => {
  if (!id || !isPlatformAdmin.value || adminEditingUser.value?.isNew) return
  try {
    const { data } = await axios.get(`/user/${id}/internal-flags`)
    if (adminEditingUser.value?._id === id) adminEditingUser.value.rmStar = data?.rmStar === true
  } catch (error) {
    console.error('Could not load internal flags', error)
  }
}, { immediate: true })

const isDialogVisible = ref(false)
const addFreeSubscription = ref(false)
const choosingProfileImage = ref(false)

// Form Errors
const errors = ref({
  profileId: undefined,
})

const clearErrors = () => {
  if (errors.value) {
    errors.value = {}
  }
}

const handleMediaSelection = selectedMedia => {
  if(selectedMedia){
    //rips out any ?t=9287439832749 that may have been added to refresh the image after cropping
    adminEditingUser.value.profileImageId = selectedMedia.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/)[0]
    choosingProfileImage.value = false
  } else{
    adminEditingUser.value.profileImageId = null
  }
}

const form = ref()
const valid = ref(true)
const onFormSubmit = async () => {
  form.value.validate().then(async ({valid: isValid}) => {
    if(isValid){
      let newId = await usersStore.saveAdminEditingUser()
      emit('update:isDialogVisible', false)
      if(addFreeSubscription.value){
        await usersStore.adminEditUserSubscription(newId)
      }
    }
  })
}

onMounted(() => {
  tagsStore.getTags()
})

// Method to update verifiedTags when the combobox changes
const updateVerifiedTags = tags => {
  adminEditingUser.value.verifiedTags = tags // Update directly if needed
}

const approveRequestedTag = tag => {
  if (!adminEditingUser.value.verifiedTags) adminEditingUser.value.verifiedTags = []

  // Add to verified if not already present
  const tagId = (tag._id || tag).toString()
  const alreadyVerified = adminEditingUser.value.verifiedTags.some(
    t => (t._id || t).toString() === tagId
  )
  if (!alreadyVerified) {
    adminEditingUser.value.verifiedTags.push(tag)
  }

  // Remove from requested
  adminEditingUser.value.requestedTags = (adminEditingUser.value.requestedTags || []).filter(
    t => (t._id || t).toString() !== tagId
  )
}

const denyRequestedTag = tag => {
  const tagId = (tag._id || tag).toString()
  adminEditingUser.value.requestedTags = (adminEditingUser.value.requestedTags || []).filter(
    t => (t._id || t).toString() !== tagId
  )
}

const groupFilteringOptions = computed(() => {
  let options = [{value:"", text:"None/Individual"}]
  for(let group of userGroups.value){
    options.push({
      value: group.name,
      text: group.name
    })
  }
  
  return options
})

//check to see if we should pop up any dialogs when the store changes
//this seems hacky
usersStore.$subscribe((mutation, state) => {
  isDialogVisible.value = ( state.adminEditingUser != null)
})

//if state.adminEditingUser changes from !state.adminEditingUser to !state.adminEditingUser then save the original state
const originalAdminEditingUser = ref(null)
watchEffect(() => {
  if(!originalAdminEditingUser.value && adminEditingUser.value){
    originalAdminEditingUser.value = JSON.parse(JSON.stringify(adminEditingUser.value))
  }
  else if(originalAdminEditingUser.value && !adminEditingUser.value){
    originalAdminEditingUser.value = null
  }
})
</script>

<template>
  <VDialog
    v-if="adminEditingUser && isPlatformAdmin"
    v-model="isDialogVisible"
    :width="dWidth"
    persistent
  >
    <VCard class="pa-sm-9 pa-5">
      <VCardItem class="text-center">
        <VCardTitle class="text-h5">
          Member
        </VCardTitle>
        <VCardSubtitle v-if="adminEditingUser.referredBy">
          Referred By: {{ adminEditingUser.referredBy }}
        </VCardSubtitle>
      </VCardItem>

      <VCardText>
        <!-- 👉 Form -->
        <VForm
          ref="form"
          class="mt-6"
          @submit.prevent="onFormSubmit"
        >
          <VRow>
            <!-- Profile Image -->
            <VCol cols="12">
              <div class="flex flex-column align-center">
                <div
                  class="admin-avatar rounded-circle mb-4"
                  @click="choosingProfileImage = !choosingProfileImage"
                >
                  <VImg
                    v-if="adminEditingUser.profileImageId"
                    class="avatar-image"
                    :src="mediaUrl(adminEditingUser.profileImageId, 'small')"
                    cover
                  />
                  <div
                    v-else-if="adminEditingUser?.firstName?.length > 0 || adminEditingUser?.lastName?.length > 0"
                    class="avatar-fallback flex flex-row align-center justify-center"
                  >
                    <span class="avatar-fallback-text">{{ adminEditingUser.firstName?.[0]?.toUpperCase() }}{{ adminEditingUser.lastName?.[0]?.toUpperCase() }}</span>
                  </div>
                  <div
                    v-else
                    class="avatar-fallback flex flex-row align-center justify-center"
                  >
                    <span class="avatar-fallback-text text-xl"><VIcon>mdi-account</VIcon></span>
                  </div>
                  <div class="avatar-camera-icon-holder">
                    <VIcon class="avatar-camera-icon">
                      mdi-camera
                    </VIcon>
                  </div>
                </div>
                <div
                  v-if="!adminEditingUser.profileImageId"
                  class="text-primary text-caption text-center cursor-pointer"
                  @click="choosingProfileImage = true"
                >
                  Add Image
                </div>
                <div
                  v-else
                  class="text-primary text-caption text-center cursor-pointer"
                  @click="choosingProfileImage = true"
                >
                  Change Image
                </div>
              </div>
              <MediaChooserAndManager
                v-model:is-dialog-visible="choosingProfileImage"
                title="Choose a Profile Image"
                :preselected-media-ids="[adminEditingUser?.profileImageId]"
                @media-selected="handleMediaSelection"
              />
            </VCol>

            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingUser.email"
                label="Email"
                autocomplete="null"
                :rules="[requiredValidator, emailValidator]"
              />
            </VCol>

            <VCol
              v-if="adminEditingUser._id"
              cols="12"
              class="pt-0"
            >
              <UserDeliveryHealthBadge :user-id="String(adminEditingUser._id)" />
            </VCol>

            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingUser.profileId"
                label="RM Profile"
                prefix="reserverm.com/m/"
                hint="lowercase letters, numbers and - only"
                :rules="[profileIdValidator]"
                :error-messages="errors.profileId"
                autocomplete="null"
                counter="40"
                maxlength="40"
                @input="clearErrors"
              />
            </VCol>

            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingUser.firstName"
                label="First Name"
                autocomplete="null"
                :rules="[nameValidator]"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingUser.lastName"
                label="Last Name"
                autocomplete="null"
                :rules="[nameValidator]"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingUser.company"
                label="Company"
                autocomplete="null"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingUser.title"
                label="Title"
                autocomplete="null"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingUser.company2"
                label="Company 2"
                autocomplete="null"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingUser.title2"
                label="Title 2"
                autocomplete="null"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextarea
                v-model="adminEditingUser.bio"
                label="Bio"
                counter="500"
                maxlength="500"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingUser.phoneNumber"
                label="Phone"
                autocomplete="null"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VTextField
                v-model="adminEditingUser.secondaryAccountEmail"
                label="Secondary Account Email"
                :disabled="adminEditingUser.isSecondaryAccount"
                autocomplete="null"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <VSelect
                v-model="adminEditingUser.membershipSource"
                label="Membership Source"
                :disabled="adminEditingUser.isSecondaryAccount"
                :items="['Advisor', 'BD', 'Brian Stanley','Comp','Corp Disc','Direct','Founding','Friend','Investor','J.P. Morgan Private Bank','Linden Capital','Magellan','MDP Direct','MDP Corporate Discount','Partner','Partner Restaurant','PEAC','RM','Shore Capital ','YPO', 'EO']"
              />
            </VCol>

            <VCol
              cols="12"
            >
              <!--              https://flatpickr.js.org/formatting/ -->
              <AppDateTimePicker
                v-model="adminEditingUser.memberSince"
                label="Member Since"
                :config="{ altInput: true, altFormat: 'n/j/y', dateFormat: 'Z' }"
                :disabled="adminEditingUser.isSecondaryAccount"
              />
            </VCol>
            <VCol
              cols="12"
            >
              <!--              https://flatpickr.js.org/formatting/ -->
              <VTextField
                v-model="adminEditingUser.networkSize"
                label="Network Size"
                type="number"
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="adminEditingUser.profileWebsiteLink"
                label="Bio page"
                type="string"
                prepend-inner-icon="mdi-web"
                hint="enter full url to company/personal website bio"
                class="mt-4"
              />
              <VTextField
                v-model="adminEditingUser.profileLinkedInProfileId"
                label="LinkedIn"
                type="string"
                prepend-inner-icon="mdi-linkedin"
                hint="enter profile id"
              />
              <VTextField
                v-model="adminEditingUser.profileInstagramProfileId"
                label="Instagram"
                type="string"
                prepend-inner-icon="mdi-instagram"
                hint="enter username"
                class="mt-4"
                prefix="@"
              />
              <VTextField
                v-model="adminEditingUser.profileXProfileId"
                label="X"
                type="string"
                prepend-inner-icon="simple-icons:x"
                hint="enter handle/username"
                class="mt-4"
                prefix="@"
              />
              <VTextField
                v-model="adminEditingUser.profileTikTokProfileId"
                label="TikTok"
                type="string"
                prepend-inner-icon="simple-icons:tiktok"
                hint="enter username"
                class="mt-4"
                prefix="@"
              />

              <VTextarea
                v-model="adminEditingUser.clubsGroups"
                class="mt-4"
                label="Clubs/Groups"
                rows="4"
                placeholder="e.g. NFLPA, YPO, Triumph Jets"
                hint="Enter clubs, groups, or organizations"
              />

              <VSelect
                v-model="adminEditingUser.restaurantAffiliation"
                :items="['Yes', 'No']"
                :menu-props="{ location: 'bottom' }"
                label="Affiliated with any restaurants?"
                hint="e.g. Investor, Executive/Partner, VIP/Club Member"
                persistent-hint
                class="mt-4"
              />
              <VTextarea
                v-if="adminEditingUser.restaurantAffiliation === 'Yes'"
                v-model="adminEditingUser.restaurantAffiliationNames"
                class="mt-4"
                label="Restaurant Affiliation Names"
                rows="2"
                placeholder="e.g. Restaurant names they're affiliated with"
                hint="Restaurant names they're affiliated with"
              />

              <VTextarea
                v-model="adminEditingUser.userCities"
                class="mt-4"
                label="Favorite Restaurants & Ones to Dine At"
                rows="4"
                placeholder="e.g. Restaurants they want to dine at"
                hint="Favorite restaurants and ones they want to dine at"
              />

              <VTextarea
                v-model="adminEditingUser.diningFrequency"
                class="mt-4"
                label="Dining Frequency (Admin Only)"
                rows="3"
                placeholder="e.g. 1-2x per week, 1-2x per month, 1-2x per quarter, 1-2x per year"
                hint="How often they dine out at favorite/top restaurants"
              />

              <VSelect
                v-model="adminEditingUser.allowSpendSharing"
                density="compact"
                label="Allow Spend Sharing with Requests"
                item-title="text"
                item-value="value"
                :items="[{text:'Yes', value: true},{text:'No', value: false}]"
              />

              <VTextarea
                v-model="adminEditingUser.testimonial"
                class="mb-2"
                label="Testimonal"
                :rules="[]"
                counter="1000"
                maxlength="1000"
              />
              <h4>Identity/Affiliation Verification</h4>
              <VCombobox
                v-model="adminEditingUser.verifiedTags"
                label="Verified Tags"
                type="string"
                prepend-inner-icon="iconoir:verified-user"
                class="mt-4"
                item-title="tagName"
                item-value="_id"
                :items="tagsStore.tags"
                chips
                closable-chips
                hide-selected
                multiple
                @change="updateVerifiedTags($event.filter(tag => tagsStore.tags.some(t => t._id === tag)))"
              />
              <div
                v-if="adminEditingUser.requestedTags?.length > 0"
                class="mt-4"
              >
                <h4 class="mb-2 text-warning">
                  Pending Tag Requests
                </h4>
                <div class="d-flex flex-wrap gap-2">
                  <VChip
                    v-for="tag in adminEditingUser.requestedTags"
                    :key="(tag._id || tag).toString()"
                    color="warning"
                    variant="tonal"
                  >
                    <VIcon
                      start
                      size="small"
                    >
                      mdi-clock-outline
                    </VIcon>
                    {{ tag.tagName || 'Unknown' }}
                    <VBtn
                      icon
                      size="x-small"
                      color="success"
                      variant="text"
                      class="ml-1"
                      title="Approve"
                      @click="approveRequestedTag(tag)"
                    >
                      <VIcon size="small">
                        mdi-check
                      </VIcon>
                    </VBtn>
                    <VBtn
                      icon
                      size="x-small"
                      color="error"
                      variant="text"
                      title="Deny"
                      @click="denyRequestedTag(tag)"
                    >
                      <VIcon size="small">
                        mdi-close
                      </VIcon>
                    </VBtn>
                  </VChip>
                </div>
              </div>
              <VTextField
                v-if="adminEditingUser.verificationInfo1Data"
                v-model="adminEditingUser.verificationInfo1Data"
                :label="'Verification - '+adminEditingUser.verificationInfo1Label"
                type="string"
                class="mt-4"
              />
              <VTextField
                v-if="adminEditingUser.verificationInfo2Data"
                v-model="adminEditingUser.verificationInfo2Data"
                :label="'Verification - '+adminEditingUser.verificationInfo2Label"
                type="string"
                class="mt-4"
              />
              <VCheckbox
                v-if="!adminEditingUser.verified"
                v-model="adminEditingUser.promptToVerify"
                label="Prompt member to provide social data"
              />
              <VCheckbox
                v-if="!adminEditingUser.verified"
                v-model="adminEditingUser.pendingVerification"
                label="Verification Pending"
              />
              <VCheckbox
                v-model="adminEditingUser.verified"
                label="Verify Member's Identity"
              />
              <div
                v-if="!adminEditingUser.verified && adminEditingUser.pendingVerification"
                style="color:blue;"
              >
                <VIcon>mdi-progress-check</VIcon>Pending Verification<br>
              </div>
            </VCol>

            <VCol
              v-if="adminEditingUser.verified"
              cols="12"
            >
              <span style="color:green;">
                <VIcon>mdi-check</VIcon>Verified<br>
              </span>
              <div v-if="originalAdminEditingUser.platinumPlanIdToGiveAfterVerification">
                Free Platinum Account ({{ originalAdminEditingUser.platinumPlanIdToGiveAfterVerification }}) will be added on save
              </div>
              <VCheckbox
                v-else-if="adminEditingUser.verified && !originalAdminEditingUser.verified"
                v-model="adminEditingUser.give1YrPlatinumAfterVerification"
                label="Give 1yr free Platinum"
              />
            </VCol>
            <VCol
              v-if="adminEditingUser.isNew"
              cols="12"
            >
              <VSelect
                v-model="adminEditingUser.addToGroup"
                class="mb-4"
                label="Add user to Group"
                item-title="text"
                item-value="value"
                :items="groupFilteringOptions"
                required
              />
            </VCol>
            <VCol
              v-if="adminEditingUser.isNew && (!adminEditingUser.addToGroup || adminEditingUser.addToGroup =='')"
              cols="12"
            >
              <VCheckbox
                v-model="addFreeSubscription"
                label="Add subscription after save"
              />
            </VCol>

            <!-- RM AI Access -->
            <VCol cols="12">
              <VCheckbox
                v-model="adminEditingUser.agentEnabled"
                label="Enable RM AI"
                hint="Uncheck to suspend this member's access to RM AI"
                persistent-hint
              />
            </VCol>

            <!-- RM-281: trusted-member exemption from the 75-min overlap rule -->
            <VCol cols="12">
              <VCheckbox
                v-model="adminEditingUser.allowConcurrentRequests"
                label="Allow multiple concurrent requests"
                hint="Lets this member submit overlapping reservation requests for the same date and time — each carries its own card hold"
                persistent-hint
              />
            </VCol>

            <!-- RM-359: Star — internal, never shown to the member -->
            <VCol
              v-if="isPlatformAdmin"
              cols="12"
            >
              <VCheckbox
                v-model="adminEditingUser.rmStar"
                label="Star (internal)"
                hint="Requests from this member never wait for RM approval. Internal to RM — the member never sees it"
                persistent-hint
                :disabled="adminEditingUser.rmStar === undefined"
              />
            </VCol>

            <!-- RM-260: skip the automated Platinum renewal emails for this member -->
            <VCol cols="12">
              <VCheckbox
                v-model="adminEditingUser.doNotSendRenewalEmails"
                label="Don't send renewal emails"
                hint="Skips the automated Platinum renewal reminders and the RM-team heads-up for this member"
                persistent-hint
              />
            </VCol>

            <!-- 👉 Submit and Cancel -->
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                :disabled="fetching"
                @click="form?.validate(); onFormSubmit()"
              >
                Save
              </VBtn>
              <VBtn
                color="secondary"
                variant="tonal"
                @click="usersStore.cancelAdminEditUser()"
              >
                Cancel
              </VBtn>
            </VCol>
          </VRow>
        </VForm>

        <!-- Member Commitment status + admin override (RM-140) -->
        <VDivider class="my-4" />
        <MemberCommitmentAdminPanel
          v-if="adminEditingUser._id"
          :user-id="String(adminEditingUser._id)"
          :initial-commitment="adminEditingUser.memberCommitment"
          :initial-override="adminEditingUser.memberCommitmentOverride"
        />

        <!-- Admin notification preferences override -->
        <VDivider class="my-4" />
        <AdminNotificationPreferences
          v-if="adminEditingUser._id"
          :user-id="String(adminEditingUser._id)"
        />

        <!-- Comms history for this user -->
        <VDivider class="my-4" />
        <div class="text-subtitle-2 mb-2 text-medium-emphasis">
          Comms History
        </div>
        <CommsHistoryPanel
          v-if="adminEditingUser._id"
          :user-id="String(adminEditingUser._id)"
          :email="adminEditingUser.email"
          :compact="true"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.admin-avatar {
  cursor: pointer;
  position: relative;
  width: 5rem;
  height: 5rem;
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 0.25rem;
}

.admin-avatar:hover {
  filter: brightness(0.9);
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  font-size: 1.4rem;
}

.avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.avatar-camera-icon-holder {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -6%;
  right: -6%;
  z-index: 10;
  padding: 0.3rem;
  background: var(--ds-surface-inset);
  border-radius: 50%;
}

.avatar-camera-icon {
  width: 1rem;
  height: 1rem;
}

.avatar-fallback-text {
  width: fit-content;
}
</style>
